import { useAuthStore } from '@/lib/auth/store'

/**
 * Per-viewer memory of the stock and broker a member last opened in Broker
 * Chirfaar.
 *
 * It exists for the three sidebar rows that need an entity the URL does not
 * carry — By stock, Broker's Choice and PV range (`nav-groups.ts`). Their
 * param-less routes (`*Redirect.tsx`) read this first and fall back to a
 * market-wide default only when it is empty, so a member who has already
 * opened one house or one name gets THAT one back, not a stranger's.
 *
 * `localStorage`, not the URL, a query or a Zustand store: it has to survive
 * the member leaving the family entirely — closing the tab, browsing Market
 * for a week — and it is read by a route LOADER, which runs before anything
 * else has mounted to hold it. Every read and write is wrapped in try/catch
 * and an unreadable or blocked store answers "none" rather than throwing: a
 * lost shortcut costs the member a default page, not a broken navigation.
 */

const LAST_STOCK_KEY = 'mm-brokers-last-stock-v1'
const LAST_BROKER_KEY = 'mm-brokers-last-broker-v1'

const read = (key: string): string | null => {
  try {
    const raw = window.localStorage.getItem(key)
    return raw === null || raw === '' ? null : raw
  } catch {
    return null
  }
}

const write = (key: string, value: string): void => {
  try {
    window.localStorage.setItem(key, value)
  } catch {
    // Blocked or full storage costs the member a remembered shortcut next
    // time, not the page they are on right now.
  }
}

/**
 * By stock and PV range share this one entry — the brief's "one axis, two
 * screens": both read a stock at a price, so opening either remembers for
 * both.
 */
export const readLastBrokerStock = (): string | null => read(LAST_STOCK_KEY)

export const rememberLastBrokerStock = (symbol: string): void => {
  write(LAST_STOCK_KEY, symbol)
}

/** `/brokers/$code` alone records this — see that route's own loader. */
export const readLastBroker = (): string | null => read(LAST_BROKER_KEY)

export const rememberLastBroker = (code: string): void => {
  write(LAST_BROKER_KEY, code)
}

const forgetLastUsed = (): void => {
  try {
    window.localStorage.removeItem(LAST_STOCK_KEY)
    window.localStorage.removeItem(LAST_BROKER_KEY)
  } catch {
    // Blocked storage never held a remembered pick to forget.
  }
}

/**
 * Last-used is one member's pick, and a browser can be shared — the same gap
 * `features/search/lib/recent-searches.ts` documents for its own recents.
 * `Me` carries no member id to key either by, so both end with the session
 * instead: any way the session ends — the profile menu, the Account hub, a
 * refresh token that no longer works — passes through `signOut`, which this
 * hears the same way that file's own subscriber does.
 */
useAuthStore.subscribe((state, previous) => {
  if (previous.status === 'authenticated' && state.status !== 'authenticated') forgetLastUsed()
})
